// ai-sidebar/components/message-thread.tsx
// State 2 (working/streaming) and the resting active thread. Renders user
// bubbles, tool steps, the streaming assistant answer, a response toolbar, and
// a graceful error card. Consumes the real SSE-driven state from useAssistant.
import { useEffect, useRef, useState } from "react";
import {
  Check, X, Loader2, Bookmark, BookmarkCheck, Copy, ThumbsUp, ThumbsDown, AlertTriangle, RefreshCw, ChevronDown, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { Orb } from "./orb";
import { Markdown, extractCitations, stripEmDashes } from "./markdown";
import { ResultCard } from "./result-card";
import { useAssistant } from "../use-assistant";
import { saveInsight, deleteInsight } from "@/app/services/reporting/insights-api-service";
import type { UIMessage, UIToolStep } from "../types";

const TOOL_LABELS: Record<string, string> = {
  read_brand_data: "Reading brand data",
  index_brand_data: "Indexing brand data",
  web_search: "Searching the web",
  send_email: "Preparing email",
  request_publish_content: "Preparing to publish",
};

function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name.replace(/_/g, " ");
}

function previewResult(result: unknown): string {
  let s: string;
  if (typeof result === "string") s = result;
  else {
    try { s = JSON.stringify(result, null, 2); } catch { s = String(result); }
  }
  return s.length > 1500 ? s.slice(0, 1500) + "\n…" : s;
}

function ToolStepRow({ step, open, onToggle }: { step: UIToolStep; open: boolean; onToggle: () => void }) {
  const teal = "var(--ai-teal-text)";
  const Icon = step.state === "done" ? Check : step.state === "failed" ? X : Loader2;
  const color =
    step.state === "done" ? teal : step.state === "failed" ? "var(--infrared-coral)" : "var(--spectral-violet)";
  const hasSource = step.state === "done" && step.result !== undefined && step.result !== null;

  return (
    <div
      className="rounded-[10px] border border-border bg-card"
      style={{ boxShadow: "var(--elevation-sm)" }}
    >
      <button
        type="button"
        disabled={!hasSource}
        onClick={() => hasSource && onToggle()}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left disabled:cursor-default"
      >
        <Icon
          className={`w-3.5 h-3.5 shrink-0 ${step.state === "running" ? "ai-spin" : ""}`}
          style={{ strokeWidth: 1.85, color }}
        />
        <span className="text-xs text-foreground">{toolLabel(step.name)}</span>
        {step.meta && <span className="text-xs text-muted-foreground">· {step.meta}</span>}
        {hasSource && (
          <>
            <span className="flex-1" />
            <span className="text-[10px] text-muted-foreground">{open ? "Hide source" : "View source"}</span>
            <ChevronDown
              className="w-3 h-3 shrink-0 text-muted-foreground transition-transform"
              style={{ transform: open ? "rotate(180deg)" : "none", strokeWidth: 1.85 }}
            />
          </>
        )}
      </button>
      {open && hasSource && (
        <pre
          className="overflow-auto text-[11px] text-muted-foreground"
          style={{
            fontFamily: "var(--font-mono)",
            margin: 0,
            padding: "8px 10px",
            borderTop: "1px solid var(--border)",
            maxHeight: 220,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {previewResult(step.result)}
        </pre>
      )}
    </div>
  );
}

function UserBubble({ content }: { content: string }) {
  return (
    <div className="flex justify-end">
      <div
        className="rounded-2xl px-3.5 py-2 text-sm text-foreground max-w-[85%]"
        style={{
          backgroundColor: "var(--alpha-teal-10)",
          border: "1px solid var(--alpha-teal-20)",
        }}
      >
        {content}
      </div>
    </div>
  );
}

function ResponseToolbar({ msg }: { msg: UIMessage }) {
  const { messages, brandName, currentPage } = useAssistant();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(stripEmDashes(msg.content));
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  // The question this answer responds to (nearest preceding user message).
  const question = (() => {
    const idx = messages.findIndex((m) => m.id === msg.id);
    for (let j = idx - 1; j >= 0; j--) if (messages[j].role === "user") return messages[j].content;
    return "";
  })();

  const save = async () => {
    if (saved || saving) return;
    const brand =
      brandName ||
      (typeof localStorage !== "undefined" ? localStorage.getItem("bm_brand") : null);
    if (!brand) {
      toast.error("Select a brand first to save insights.");
      return;
    }
    setSaving(true);
    const title = (question || "Assistant answer").slice(0, 120);
    try {
      const cleaned = stripEmDashes(msg.content);
      const result = await saveInsight({
        brand_name: brand,
        module: "assistant",
        title,
        subtitle: cleaned.replace(/\s+/g, " ").trim().slice(0, 200),
        saved_from: "Page Assistant",
        raw_data: { question, answer: cleaned, page: currentPage },
      });
      setSaved(true);
      toast.success("Saved to Insights", {
        description: title,
        cancel: {
          label: "Undo",
          onClick: async () => {
            try {
              if (result.id) await deleteInsight(result.id);
              setSaved(false);
            } catch {
              /* non-fatal */
            }
          },
        },
      });
    } catch (e: any) {
      toast.error(e?.message || "Could not save to insights.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1 pt-1">
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="ai-save-btn inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs disabled:opacity-60"
        style={{ color: saved ? "var(--spectral-violet)" : "var(--muted-foreground)" }}
      >
        {saved ? (
          <BookmarkCheck className="w-3.5 h-3.5" style={{ strokeWidth: 1.85 }} />
        ) : (
          <Bookmark className="w-3.5 h-3.5" style={{ strokeWidth: 1.85 }} />
        )}
        {saved ? "Saved" : "Save to insight"}
      </button>
      <span className="flex-1" />
      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Copy" onClick={copy}>
        <Copy className="w-3.5 h-3.5" style={{ strokeWidth: 1.85 }} />
      </Button>
      {/* No feedback endpoint exists yet: log locally and don't claim the
          rating was recorded anywhere. */}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        aria-label="Good response"
        onClick={() => {
          console.info("Assistant feedback (not persisted):", { rating: "up", messageId: msg.id, page: currentPage });
          toast("Thanks. Feedback isn't saved to your account yet.");
        }}
      >
        <ThumbsUp className="w-3.5 h-3.5" style={{ strokeWidth: 1.85 }} />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        aria-label="Bad response"
        onClick={() => {
          console.info("Assistant feedback (not persisted):", { rating: "down", messageId: msg.id, page: currentPage });
          toast("Thanks. Feedback isn't saved to your account yet.");
        }}
      >
        <ThumbsDown className="w-3.5 h-3.5" style={{ strokeWidth: 1.85 }} />
      </Button>
    </div>
  );
}

function ErrorCard() {
  const { send, messages, errorText } = useAssistant();
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  return (
    <div
      className="rounded-2xl px-3.5 py-3 space-y-2"
      style={{ backgroundColor: "var(--infrared-coral-10)", border: "1px solid var(--infrared-coral)" }}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" style={{ strokeWidth: 1.85, color: "var(--infrared-coral)" }} />
        <span className="text-sm font-semibold text-foreground">I couldn't complete that</span>
      </div>
      <p className="text-xs text-muted-foreground">
        {errorText || "The response was interrupted. You can try again."}
      </p>
      <div className="flex gap-2 pt-0.5">
        <Button
          variant="outline"
          size="sm"
          className="h-7"
          onClick={() => lastUser && send(lastUser.content)}
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" style={{ strokeWidth: 1.85 }} />
          Try again
        </Button>
      </div>
    </div>
  );
}

function SourcesFootnote({
  labels,
  fallback,
  onReveal,
  interactive,
}: {
  labels: string[];
  fallback: string | null;
  onReveal: () => void;
  interactive: boolean;
}) {
  if (labels.length === 0) {
    if (!fallback) return null;
    return interactive ? (
      <button
        type="button"
        className="ai-source-link text-xs text-left"
        style={{ color: "var(--spectral-violet)" }}
        onClick={onReveal}
      >
        {fallback}
      </button>
    ) : (
      <div className="text-xs" style={{ color: "var(--spectral-violet)" }}>{fallback}</div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground">
      Drawn from{" "}
      {labels.map((l, i) => (
        <span key={l}>
          {interactive ? (
            <button
              type="button"
              className="ai-source-link"
              style={{ color: "var(--spectral-violet)", fontWeight: 500 }}
              onClick={onReveal}
            >
              {l}
            </button>
          ) : (
            <span style={{ color: "var(--spectral-violet)", fontWeight: 500 }}>{l}</span>
          )}
          {i < labels.length - 2 ? ", " : i === labels.length - 2 ? " and " : ""}
        </span>
      ))}
    </div>
  );
}

function groundedFootnote(msg: UIMessage): string | null {
  const done = (msg.tools ?? []).filter((t) => t.state === "done");
  if (done.length === 0) return null;
  const hasBrand = done.some((t) => t.name === "read_brand_data" || t.name === "index_brand_data");
  const hasWeb = done.some((t) => t.name === "web_search");
  if (hasBrand && hasWeb) return "Drawn from your brand data and the web";
  if (hasBrand) return "Drawn from your brand data";
  if (hasWeb) return "Drawn from the web";
  return null;
}

function AssistantMessage({ msg }: { msg: UIMessage }) {
  const thinking = msg.streaming && msg.content === "" && (msg.tools ?? []).length === 0;
  // A citation is only real when this answer actually captured source data this
  // turn. If it didn't (e.g. it reused earlier context), strip the markers so
  // there are no chips that look clickable but have nothing to reveal.
  const hasAnySource = (msg.tools ?? []).some((t) => t.state === "done" && t.result != null);
  const cites = hasAnySource ? extractCitations(msg.content) : [];
  const displayContent = hasAnySource ? msg.content : msg.content.replace(/\[\[cite:[^\]]+\]\]/g, "");
  const fallback = groundedFootnote(msg);
  // Clicking a source reveals the underlying data in place (no page navigation).
  const [sourceOpen, setSourceOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  const reveal = () => {
    setSourceOpen(true);
    requestAnimationFrame(() => toolsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
  };
  return (
    <div className="flex gap-2.5">
      <Orb size={26} working={!!msg.streaming} />
      <div className="flex-1 min-w-0 space-y-2">
        {(msg.tools ?? []).length > 0 && (
          <div ref={toolsRef} className="space-y-1.5">
            {msg.tools!.map((t) => (
              <ToolStepRow key={t.id} step={t} open={sourceOpen} onToggle={() => setSourceOpen((o) => !o)} />
            ))}
          </div>
        )}

        {thinking ? (
          <div className="flex items-center gap-1 py-1">
            <span className="ai-dot w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--spectral-violet)" }} />
            <span className="ai-dot w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--spectral-violet)" }} />
            <span className="ai-dot w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--spectral-violet)" }} />
          </div>
        ) : (
          msg.content !== "" && (
            <div
              className={hasAnySource ? undefined : "ai-cites-static"}
              onClick={(e) => {
                if (hasAnySource && (e.target as HTMLElement).closest("[data-ai-cite]")) reveal();
              }}
            >
              <Markdown text={displayContent} />
              {msg.streaming && (
                <span
                  className="ai-caret inline-block w-[2px] h-4 align-middle"
                  style={{ backgroundColor: "var(--spectral-violet)" }}
                />
              )}
            </div>
          )
        )}

        {!msg.streaming && (cites.length > 0 || fallback) && (
          <SourcesFootnote labels={cites} fallback={fallback} onReveal={reveal} interactive={hasAnySource} />
        )}

        {msg.resultCard && <ResultCard data={msg.resultCard} />}

        {msg.reportBack && (
          <div className="flex items-center gap-1.5 text-muted-foreground" style={{ fontSize: 11.5, paddingLeft: 2 }}>
            <Clock style={{ width: 13, height: 13, strokeWidth: 1.85 }} />
            {msg.reportBack}
          </div>
        )}

        {msg.error && <ErrorCard />}

        {msg.complete && !msg.error && msg.content !== "" && <ResponseToolbar msg={msg} />}
      </div>
    </div>
  );
}

function FollowUpChips({ onPick }: { onPick: (t: string) => void }) {
  const chips = ["Show me the data", "How do I fix it?", "Make it shorter"];
  return (
    <div className="flex flex-wrap gap-2 pl-[34px]">
      {chips.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onPick(c)}
          className="ai-followup text-xs text-foreground bg-card"
          style={{ padding: "6px 12px", borderRadius: 999, border: "1px solid var(--border)" }}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

export function MessageThread() {
  const { messages, send } = useAssistant();
  const scrollRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);

  // Auto-scroll only when the user is already near the bottom (don't fight a
  // user who scrolled up to read). Never use scrollIntoView.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const lastLen = messages[messages.length - 1]?.content.length ?? 0;
  useEffect(() => {
    const el = scrollRef.current;
    if (el && nearBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, lastLen]);

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="flex-1 min-h-0 overflow-y-auto space-y-4"
      style={{ padding: 18 }}
    >
      {messages.map((m) =>
        m.role === "user" ? (
          <UserBubble key={m.id} content={m.content} />
        ) : (
          <AssistantMessage key={m.id} msg={m} />
        ),
      )}
      {(() => {
        const last = messages[messages.length - 1];
        return last && last.role === "assistant" && last.complete && !last.error && last.content !== "" ? (
          <FollowUpChips onPick={send} />
        ) : null;
      })()}
    </div>
  );
}
